
import { itemFields } from './ItemFields.mjs';
import { databaseDateTimeString, dateStringToDate } from './Common.mjs';
import * as TaskBuckets from './TaskBuckets.mjs';
import { hasFieldValue } from './Common.mjs';
import { fullUrlForTaskId, fullUrlForItemCounter } from './Urls.mjs';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
dayjs.extend(utc);
dayjs.extend(customParseFormat);

// Constants used in email subcriptions

// Used as a MessageBody in my SQS queues to identify type of email to be sent.
export const emailTypeItemStatus = 'itemStatus';
// Per-task transition digest (one row per item per subscriber per day).
export const emailTypeItemTransition = 'itemTransition';

// Used for sendEveryDays.
export const emailSendDaily = 1;
export const emailSendWeekly = 7;
export const emailSendMonthly = 30;
export const emailSendQuarterly = 90;
export const emailSendEverySixMonths = 180;
export const emailSendYearly = 365;

// `digestMode` values written to TaskPogoEmailSubscription.digestMode. The
// daemon gates the two independent mail paths off this single field:
//
//   summary     -> periodic status digest only (nextSendDate + sendEveryDays)
//   transitions -> per-task worsening alerts only (no scheduled status mail)
//   both        -> status digest AND transition alerts
//
// Legacy rows (digestMode null) fall back to `effectiveDigestMode` below.
export const digestModeSummary = 'summary';
export const digestModeTransitions = 'transitions';
export const digestModeBoth = 'both';

// Resolve the user's intent from a subscription row, tolerating legacy rows
// that predate the digestMode field. Used by both the React UI and the
// email daemon so the two stay in lockstep.
export function effectiveDigestMode(subscription) {
    const mode = subscription && subscription.digestMode;
    if (mode === digestModeSummary
        || mode === digestModeTransitions
        || mode === digestModeBoth) {
        return mode;
    }
    // Legacy fallback: a row written before the field existed. Treat
    // notifyOnTaskStateChange=true as "both" (the only way to get
    // transition mail before this change) and everything else as plain
    // periodic status.
    return (subscription && subscription.notifyOnTaskStateChange)
        ? digestModeBoth
        : digestModeSummary;
}

export function digestModeWantsSummary(mode) {
    return mode === digestModeSummary || mode === digestModeBoth;
}

export function digestModeWantsTransitions(mode) {
    return mode === digestModeTransitions || mode === digestModeBoth;
}


// Used with copyOnlyKnownFields for database saving.
export const emailFields = [
  {name: "ID", key: "id", type: "text"},
  {name: "emailType", key: "emailType", type: "text"},
  {name: "email", key: "email", type: "text"},
  {name: "name", key: "name", type: "text"},
  {name: "sendHourGMT", key: "sendHourGMT", type: "number"},
  {name: "itemId", key: "itemId", type: "text"},
  {name: "sendEveryDays", key: "sendEveryDays", type: "number"},
  {name: "lastSentDate", key: "lastSentDate", type: "text"},
  {name: "nextSendDate", key: "nextSendDate", type: "text"},
  {name: "unsubscribeKey", key: "unsubscribeKey", type: "text"},
  {name: "notifyOnTaskStateChange", key: "notifyOnTaskStateChange", type: "boolean"},
  {name: "taskBucketSnapshots", key: "taskBucketSnapshots", type: "text"},
  {name: "transitionLastSentDate", key: "transitionLastSentDate", type: "text"},
  {name: "transitionNextSendDate", key: "transitionNextSendDate", type: "text"},
  {name: "digestMode", key: "digestMode", type: "text"}
];

// Given a subscription, calculate the next Send date assuming just sent. Returns in databaseDate format.
export function calculateNextSendDate(subscription) {
    let sendEveryDays = subscription.sendEveryDays;
    let sendHourGMT = subscription.sendHourGMT;
    let now = dayjs();
    let nextSendDate;

    switch (sendEveryDays) {
    case emailSendDaily:
    case emailSendWeekly:
    default:
        // These guys don't need any special handling, just add the # of days:
        nextSendDate = now.add(sendEveryDays, 'day');
        break;
    case emailSendMonthly:
        // Monthly varies # of days based on a 28, 29, 30, or 31 day month... Dayjs handles.
        // Note if say on 1/31, and bump a month it'll set the day to 2/28.
        nextSendDate = now.add(1, 'month');
        break;
    case emailSendQuarterly:
        // Add 3 months... Same day of month (or earlier if new month is shorter and end of this month).
        nextSendDate = now.add(3, 'month');
        break;
    case emailSendEverySixMonths:
        // Add 6 months... Same day of month (or earlier if new month is shorter and end of this month)..
        nextSendDate = now.add(6, 'month');
        break;
    case emailSendYearly:
        // Add a year to today.
        nextSendDate = now.add(1, 'year');
        break;
    }

    let nextSendDateTime = nextSendDate.hour(sendHourGMT).minute(0).second(0).millisecond(0);

    return databaseDateTimeString(nextSendDateTime);
}

// Transition digests always go out once a day. Mirrors `calculateNextSendDate`
// for the hour-of-day handling (dayjs `.hour()` is local time, see the existing
// Email test commentary).
export function calculateNextTransitionSendDate(subscription) {
  const sendHourGMT = subscription.sendHourGMT ?? 0;
  const next = dayjs().add(1, 'day').hour(sendHourGMT).minute(0).second(0).millisecond(0);
  return databaseDateTimeString(next);
}

// Compute per-task transitions between two bucket snapshots. Returns:
//   { digestRows: [{taskId, name, from, to}], newSnapshots: {[taskId]: bucketName} }
//
// `previousSnapshots` is the {taskId -> bucketName} map persisted on the last
// digest (or {} on the lazy first-run). Worsening transitions (severity goes
// up) become digest rows; improvements are silent but still update the
// snapshot so the next worsening is detected against the latest state.
//
// `bucketName` in the snapshot uses the canonical names from TaskBuckets,
// except non-overdue/non-soon tasks collapse to `nameNotDue` (severity 0)
// rather than `nameAll` (which is a render-time grouping, not a real state).
export function computeBucketTransitions(item, previousSnapshots) {
  const prior = previousSnapshots || {};
  const tasks = (item && item.tasks && item.tasks.items) ? item.tasks.items : [];

  const counterMap = TaskBuckets.createCounterMapFromItem(item);
  TaskBuckets.updateTaskBucketNameInTaskList(tasks, counterMap, item);

  const toSnapshot = (bucketName) => {
    if (bucketName === TaskBuckets.nameOverdue) return TaskBuckets.nameOverdue;
    if (bucketName === TaskBuckets.nameSoon) return TaskBuckets.nameSoon;
    return TaskBuckets.nameNotDue;
  };

  const newSnapshots = {};
  const digestRows = [];

  for (const task of tasks) {
    const currentBucket = toSnapshot(task.bucketName);
    newSnapshots[task.id] = currentBucket;

    const priorBucket = prior[task.id] ?? TaskBuckets.nameNotDue;
    if (TaskBuckets.bucketSeverity(currentBucket) > TaskBuckets.bucketSeverity(priorBucket)) {
      digestRows.push({
        taskId: task.id,
        name: task.name,
        from: priorBucket,
        to: currentBucket
      });
    }
  }

  return { digestRows, newSnapshots };
}

// Builds the subject line for a task-state-change (transition) email. The
// wording reflects which buckets tasks worsened into: overdue, due soon, or
// both. `task` is singularized only when exactly one task changed overall.
export function transitionSubject({ itemName, overdueCount, soonCount }) {
  const total = overdueCount + soonCount;
  const noun = total > 1 ? 'tasks' : 'task';
  let phrase;
  if (overdueCount > 0 && soonCount > 0) {
    phrase = `${noun} overdue and due soon`;
  } else if (overdueCount > 0) {
    phrase = `${noun} overdue`;
  } else {
    phrase = `${noun} due soon`;
  }
  return `${itemName}: ${phrase}`;
}

/***** Helper functions for taking info out of an Item for use by the backend Email functions *******/

// The item-info table in the status digest. Driven by the same itemFields
// registry the edit form and EquipmentInfoUI use, so a field added there shows
// up here the moment the daemon's selection set carries it.
//
// The select branch mirrors EquipmentInfoUI's for the same reason: a select
// stores an id and displays a name, so without it a digest could mail out
// "Type: boat". It is here even though `itemType` is deliberately absent from
// the email daemon's selection set today -- being correct only because a
// column happens not to be selected is the coupling that breaks the day
// someone extends that query.
export function emailItemInfo(item) {
    return itemFields.filter( (field) => {
        return hasFieldValue(item[field.key]);
    }).map( (field) => {
        const value = field.type === 'select'
            ? (field.selects?.find((option) => option.id === item[field.key])?.name ?? item[field.key])
            : item[field.key];
        return {name: field.name, value};
    });
}

export function emailCounters(item) {
    return item.itemCounter.items.map( (counter) => {
        return {
            id: counter.id,
            name: counter.name,
            value: counter.counter,
            url: fullUrlForItemCounter(item.id, counter.id),
        };
    });
}

export function emailTasks(item) {
    // Tasks buckets -- overdue, soon, all.
    let buckets = TaskBuckets.taskBuckets(item);

    return buckets.map( (bucket) => {
        return { name: bucket.name, value: bucket.tasks.length }
    });
}

// Inline email-safe colors mirroring `src/styles/tokens.css` (--tp-danger,
// --tp-warning). SES templates can't load CSS, so we ship the hex value
// per row instead of a class name.
export const emailOverdueColor = '#dc2626';
export const emailSoonColor = '#d97706';

// Shape the overdue + due-soon task buckets for the periodic status digest.
// Returns up to `limitPerBucket` linked task entries per bucket along with
// the bucket totals so the template can render "+N more" when truncated.
//
// `excludeTaskIds` (a Set or array) drops those tasks BEFORE the lists are
// counted or sliced, so the totals and the "+N more" overflow describe only
// the tasks that remain. The transition email uses it to list the item's
// OTHER overdue / due-soon tasks beside the ones that just changed.
export function emailDigestTaskLists(item, limitPerBucket = 10, { excludeTaskIds } = {}) {
    const buckets = TaskBuckets.taskBuckets(item);

    const overdueBucket = buckets.find((b) => b.name === TaskBuckets.nameOverdue);
    const soonBucket = buckets.find((b) => b.name === TaskBuckets.nameSoon);

    const excluded = new Set(excludeTaskIds ?? []);
    const keep = (task) => !excluded.has(task.id);

    const overdueTasks = (overdueBucket?.tasks ?? []).filter(keep);
    const soonTasks = (soonBucket?.tasks ?? []).filter(keep);

    const shape = (task, color) => ({
        id: task.id,
        name: task.name,
        url: fullUrlForTaskId(item.id, task.id),
        color,
    });

    return {
        overdueTasks: overdueTasks.slice(0, limitPerBucket).map((t) => shape(t, emailOverdueColor)),
        soonTasks: soonTasks.slice(0, limitPerBucket).map((t) => shape(t, emailSoonColor)),
        overdueTotal: overdueTasks.length,
        soonTotal: soonTasks.length,
        overdueTruncated: overdueTasks.length > limitPerBucket,
        soonTruncated: soonTasks.length > limitPerBucket,
        overdueOverflow: Math.max(0, overdueTasks.length - limitPerBucket),
        soonOverflow: Math.max(0, soonTasks.length - limitPerBucket),
    };
}

// The "Other tasks needing attention" section of a transition email: the
// item's overdue / due-soon tasks MINUS the ones that just changed (those are
// the email's own rows, and a task that just moved to Due Soon must not be
// listed again as "other due soon"). Same bucketing and row shape as the
// status digest, under `other*` keys so the template cannot confuse them
// with `overdueTransitions` / `soonTransitions`. `hasOtherTasks` is
// precomputed because SES's Handlebars has no `or` helper and the section
// heading needs "either list is non-empty".
export function emailTransitionTaskLists(item, transitions, limitPerBucket = 10) {
    const changed = new Set((transitions ?? []).map((t) => t.taskId));
    const lists = emailDigestTaskLists(item, limitPerBucket, { excludeTaskIds: changed });

    return {
        otherOverdueTasks: lists.overdueTasks,
        otherSoonTasks: lists.soonTasks,
        otherOverdueTotal: lists.overdueTotal,
        otherSoonTotal: lists.soonTotal,
        otherOverdueTruncated: lists.overdueTruncated,
        otherSoonTruncated: lists.soonTruncated,
        otherOverdueOverflow: lists.overdueOverflow,
        otherSoonOverflow: lists.soonOverflow,
        hasOtherTasks: lists.overdueTotal > 0 || lists.soonTotal > 0,
    };
}

// Counts completed-task entries that fall in the last 7 / 30 / 365 days.
// `entries` is the raw `items` array from a `completedTasksByItemId` query;
// each row needs a `date` AWSDateTime string.
export function emailLogbookSummary(entries, now = new Date()) {
    const counts = { week: 0, month: 0, year: 0 };
    if (!Array.isArray(entries)) {
        return counts;
    }

    const nowMs = now.getTime();
    const weekCutoff = nowMs - 7 * 24 * 60 * 60 * 1000;
    const monthCutoff = nowMs - 30 * 24 * 60 * 60 * 1000;
    const yearCutoff = nowMs - 365 * 24 * 60 * 60 * 1000;

    for (const entry of entries) {
        const date = dateStringToDate(entry?.date);
        if (!date) continue;
        const ms = date.getTime();
        if (ms < yearCutoff) continue;
        counts.year += 1;
        if (ms >= monthCutoff) counts.month += 1;
        if (ms >= weekCutoff) counts.week += 1;
    }

    return counts;
}

// Legacy helper kept for backwards compatibility / tests. The new status
// digest uses emailLogbookSummary instead.
export function emailLogbook(item)  {
    return item.completedTasks.items.map( (task) => {
        return { name: task.date, value: task.name };
    });
}